import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as workOrdersApi from '../api/workOrders';

export function WorkOrders() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [workOrders, setWorkOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = {};
      if (filters.status) activeFilters.status = filters.status;
      if (filters.priority) activeFilters.priority = filters.priority;
      if (filters.type) activeFilters.type = filters.type;
      
      const result = await workOrdersApi.getWorkOrders(selectedOrg.id, activeFilters);
      setWorkOrders(result.workOrders);
      setTotal(result.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = (workOrderId) => {
    navigate(`/service/work-orders/${workOrderId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { bg: '#c6f6d5', color: '#276749' };
      case 'in_progress': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'open': return { bg: '#fefcbf', color: '#975a16' };
      case 'cancelled': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: '#fed7d7', color: '#c53030' };
      case 'medium': return { bg: '#fefcbf', color: '#975a16' };
      case 'low': return { bg: '#c6f6d5', color: '#276749' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view work orders.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Work Orders"
        subtitle="View and track service work orders"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={styles.filterSelect}
            data-testid="status-filter"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Priority</label>
          <select
            value={filters.priority}
            onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
            style={styles.filterSelect}
            data-testid="priority-filter"
          >
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Type</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            style={styles.filterSelect}
            data-testid="type-filter"
          >
            <option value="">All</option>
            <option value="repair">Repair</option>
            <option value="maintenance">Maintenance</option>
            <option value="inspection">Inspection</option>
            <option value="installation">Installation</option>
          </select>
        </div>
        <div style={styles.totalCount}>
          {total} work order{total !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading work orders...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="work-orders-table">
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Asset</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Priority</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Assigned To</th>
                <th style={styles.th}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map(wo => {
                const statusStyle = getStatusColor(wo.status);
                const priorityStyle = getPriorityColor(wo.priority);
                return (
                  <tr
                    key={wo.id}
                    style={styles.tr}
                    onClick={() => handleRowClick(wo.id)}
                    data-testid={`wo-${wo.id}`}
                  >
                    <td style={styles.td}><code style={styles.woId}>{wo.id}</code></td>
                    <td style={styles.td}><strong>{wo.title}</strong></td>
                    <td style={styles.td}>{wo.siteName}</td>
                    <td style={styles.td}>{wo.assetName}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: statusStyle.bg, color: statusStyle.color }}>
                        {wo.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: priorityStyle.bg, color: priorityStyle.color }}>
                        {wo.priority}
                      </span>
                    </td>
                    <td style={styles.td}>{wo.type}</td>
                    <td style={styles.td}>{wo.assignedTo || '-'}</td>
                    <td style={styles.td}>{new Date(wo.updatedAt).toLocaleDateString()}</td>
                  </tr>
                );
              })}
              {workOrders.length === 0 && (
                <tr>
                  <td colSpan="9" style={styles.emptyState}>No work orders found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '120px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  woId: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '4px', fontSize: '0.75rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' }
};
